// Dated rate/floor lookup for the RCTI. See netlify/functions/rate-activations-sb.js
// for why activations exist and how they are written.
//
// Activations pin a rate set to a PERIOD START, never a mid-period date — the engine
// bills a whole 28-day period on one basis, so a mid-period effective date could not be
// honoured and is deliberately not modelled.

export const BASELINE_FROM = '1900-01-01';

// The activation in force for a period, or null when the driver has none (→ the caller
// falls back to the live rates, i.e. behaviour is unchanged for every un-activated
// driver). ISO dates compare correctly as strings.
export function activationFor(list, periodStartISO) {
  if (!Array.isArray(list) || !list.length || !periodStartISO) return null;
  let best = null;
  for (const a of list) {
    if (!a || !a.effectiveFromISO || a.effectiveFromISO > periodStartISO) continue;
    if (!best || a.effectiveFromISO > best.effectiveFromISO) best = a;
  }
  return best;
}

// True once a driver is on dated rates — used to show that the RCTI is no longer reading
// the live sheet for them.
export function isActivated(list) {
  return Array.isArray(list) && list.some((a) => a && !a.baseline);
}

// The real (non-baseline) activations, newest first, for display.
export function activationHistory(list) {
  return (Array.isArray(list) ? list : [])
    .filter((a) => a && !a.baseline)
    .sort((a, b) => (b.effectiveFromISO || '').localeCompare(a.effectiveFromISO || ''));
}

// ── Signed-contract vs billing safety net ────────────────────────────────────
//
// A signed subcontractor agreement is only a document — nothing moves its rates into
// billing until someone runs Activate to RCTI. CHEEMAM2 sat signed-but-unactivated for
// almost a month (billed his old $2.00 against a signed $2.388) because no screen
// compared the two. These checks are that comparison.

// The agreement's Schedule 1 rates mapped onto the RCTI rate keys. Extra-rate labels are
// free text on the contract form ("Express Post (EP)" / "Express" / "DoE" / …), so match
// loosely; a label we can't place is skipped rather than guessed.
export function contractRateMap(agreement) {
  const m = {};
  const base = Number(agreement?.baseRate);
  if (isFinite(base) && base > 0) m.Parcel = base;
  for (const ex of agreement?.extraRates || []) {
    const label = String(ex?.label || '').toLowerCase();
    const amount = Number(ex?.amount);
    if (!(isFinite(amount) && amount > 0)) continue;
    if (label.includes('express') || /\bep\b/.test(label)) m.Exp = amount;
    else if (label.includes('depot')) m['Depot Bags'] = amount;
    else if (label.includes('doe') || label.includes('education')) m.DOE = amount;
  }
  return m;
}

// Below half a cent is a rounding-convention delta, not a pricing decision — contracts
// carry EP as both 3.636 ($4.00 inc) and its rounded 3.64, and flagging that forever
// would teach people to ignore the panel.
const RATE_TOLERANCE = 0.005;

// Compare a signed agreement against what the RCTI would actually bill this driver.
// `activationList` is the driver's rate_activations entry; `liveRates` his Rates-sheet
// row (the fallback the RCTI reads when no activation exists — and the ONLY source for
// the special network builds, which never read activations).
//
// Returns null when billing honours the contract, else:
//   { kind: 'mismatch', source, diffs: [{ key, contract, billed }] }  — money is wrong
//   { kind: 'unpinned', source: 'sheet', diffs: [] }                 — rates agree today,
//     but only via the live sheet: nothing pins them to a dated activation, so a later
//     rate edit silently re-prices history (the exact bug activations exist to stop).
export function contractBillingIssue(agreement, activationList, liveRates) {
  const want = contractRateMap(agreement);
  if (!Object.keys(want).length) return null; // contract carries no numeric rates
  const [newest] = activationHistory(activationList);
  const billed = newest ? newest.rates || {} : liveRates || {};
  const diffs = [];
  for (const key of Object.keys(want)) {
    const b = Number(billed[key]);
    if (!isFinite(b) || Math.abs(b - want[key]) > RATE_TOLERANCE) {
      diffs.push({ key, contract: want[key], billed: isFinite(b) ? b : null });
    }
  }
  if (diffs.length) return { kind: 'mismatch', source: newest ? 'activation' : 'sheet', diffs };
  if (!newest) return { kind: 'unpinned', source: 'sheet', diffs: [] };
  return null;
}

// The panel rows: every driver whose LATEST signed agreement isn't honoured by billing.
// `agreements` = all agreements; `activations` = the whole rate_activations map;
// `ratesMap` = the live Rates sheet map; `isActiveLogin(login)` filters out leavers,
// whose signed-but-never-activated contracts are history, not a problem to fix.
export function contractBillingGaps(agreements, activations, ratesMap, isActiveLogin) {
  const latestSigned = new Map();
  for (const a of agreements || []) {
    if (!a || a.status !== 'signed') continue;
    const login = (a.driverLogin || '').toUpperCase();
    if (!login || (isActiveLogin && !isActiveLogin(login))) continue;
    const cur = latestSigned.get(login);
    const stamp = (x) => x.signedAt || x.createdAt || '';
    if (!cur || stamp(a) > stamp(cur)) latestSigned.set(login, a);
  }
  const rows = [];
  latestSigned.forEach((a, login) => {
    const issue = contractBillingIssue(
      a,
      (activations || {})[login],
      (ratesMap || {})[login] || (ratesMap || {})[a.driverLogin] || {}
    );
    if (issue) rows.push({ login, agreement: a, ...issue });
  });
  // Wrong money first; within a kind, the oldest signing has waited longest.
  rows.sort((x, y) => (x.kind === y.kind
    ? (x.agreement.signedAt || '').localeCompare(y.agreement.signedAt || '')
    : (x.kind === 'mismatch' ? -1 : 1)));
  return rows;
}
